import type { entities } from "@fraction/shared";
import {
  documentWorkspaceTargetFromSearchParams,
  documentWorkspaceTargetSearchParams,
} from "src/components/DocumentWorkspace/documentWorkspaceTarget";
import {
  type DetachedWindowPrewarmOptions,
  openDetachedWindow,
  prewarmDetachedWindow,
} from "src/lib/runtime/detachedWindows";
import { uploadedDocumentDisplayName } from "src/utilities/documentTypeNames";

export const DOCUMENT_WORKSPACE_SURFACE = "document-workspace";

export interface DocumentWorkspaceWindowPayload {
  initialFile: entities.UploadedFile & { documentUrl?: string };
  applicationId?: string;
  href?: string;
  filePath?: string;
  /** Canonical URL params describing the document, page, and optional highlighted rectangle. */
  targetSearch?: string;
}

const DOCUMENT_WORKSPACE_WINDOW_OPTIONS: DetachedWindowPrewarmOptions = {
  surface: DOCUMENT_WORKSPACE_SURFACE,
  title: "Document Workspace",
  width: 1280,
  height: 860,
  minWidth: 900,
  minHeight: 620,
  placement: "cascade",
};

export function prewarmDocumentWorkspaceWindow(): Promise<void> {
  return prewarmDetachedWindow(DOCUMENT_WORKSPACE_WINDOW_OPTIONS);
}

export function openDetachedDocumentWorkspaceWindow(
  payload: DocumentWorkspaceWindowPayload
): Promise<string> {
  const applicationId = payload.applicationId || payload.initialFile.applicationId;
  const suppliedTarget = documentWorkspaceTargetFromSearchParams(payload.targetSearch ?? "");
  const normalizedPayload: DocumentWorkspaceWindowPayload = {
    ...payload,
    ...(applicationId ? { applicationId } : {}),
    targetSearch: documentWorkspaceTargetSearchParams({
      ...suppliedTarget,
      documentId: suppliedTarget.documentId || payload.initialFile.id,
    }).toString(),
  };
  // One window per document: opening a second document leaves the first one open beside it rather
  // than swapping the doc inside it. Re-opening the same document (jumping to another source line,
  // say) reuses and focuses the window that already holds it.
  const documentKey = payload.initialFile.id || payload.initialFile.s3Document?.name;
  const workspaceId = documentKey ? `document:${documentKey}` : `application:${applicationId || "unknown"}`;
  const title = uploadedDocumentDisplayName(payload.initialFile, "Document Workspace");
  return openDetachedWindow({
    ...DOCUMENT_WORKSPACE_WINDOW_OPTIONS,
    instanceKey: workspaceId,
    payload: normalizedPayload,
    title,
  });
}
